import { ChangeEvent, useEffect, useRef, useState } from 'react'
import { ObjectHelper } from '../../../helpers/ObjectHelper'
import { usePopup } from '../../common/Popup/PopupContext'
import { PopupType } from '../../common/Popup/Popup'

interface IDragndropSectionProps {
  title: string
  descriptions: string[]
  names: string[]
  hidden?: boolean
  disabled: boolean
  handleChange: (documentsToUpload: { [name: string]: string }) => void
}

// Max file size in bytes
const MAX_FILE_SIZE = 1024000 * 5 // 5 MB

export const DragndropSection = (props: IDragndropSectionProps) => {
  const [documentsToUpload, setDocumentsToUpload] = useState<{ [name: string]: string }>({})
  const documentsToUploadRef = useRef(documentsToUpload)
  const popup = usePopup()
  const allowedExtensions = ['pdf']

  let loading = false

  useEffect(() => {
    if (Object.keys(documentsToUpload).length > props.names.length) {
      removeFile(Object.keys(documentsToUpload)[Object.keys(documentsToUpload).length - 1])
    }
  }, [props.names])

  useEffect(() => {
    documentsToUploadRef.current = documentsToUpload
    props.handleChange(documentsToUpload)
  }, [documentsToUpload])

  const removeFile = (name: string) => {
    if (props.disabled) {
      return
    }

    const newDocumentsToUpload = ObjectHelper.copy(documentsToUpload)

    newDocumentsToUpload[name] = ''
    props.handleChange(newDocumentsToUpload)

    delete newDocumentsToUpload[name]
    setDocumentsToUpload(newDocumentsToUpload)
  }

  const handleDrop = (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()
    e.stopPropagation()
    const file = e.target.files && e.target.files[0]

    if (props.disabled || !file) {
      return
    }

    const newDocumentsToUpload = ObjectHelper.copy(documentsToUploadRef.current) || []

    if (file) {
      if (file.size > MAX_FILE_SIZE || loading) {
        popup?.pushPopup('The selected file is too large.', PopupType.Error)
        return
      }

      if (!allowedExtensions.some((e) => file.name.toLowerCase().includes(e))) {
        popup?.pushPopup('Only the following extensions are allowed: ' + allowedExtensions.join(', '), PopupType.Error)
        return
      }

      loading = true

      const reader = new FileReader()

      reader.onload = () => {
        const dataUrl = reader.result ?? ''
        const value = `${file.name}|${dataUrl.toString()}`
        const names = props.names.filter((n) => !newDocumentsToUpload[n] || newDocumentsToUpload[n] === '')

        if (names.length) {
          const name = names[0]

          newDocumentsToUpload[name] = value

          setDocumentsToUpload(newDocumentsToUpload)
        }

        loading = false
      }

      reader.readAsDataURL(file)
    }

    e.target.value = ''
  }

  return (
    <div
      className={
        'fade ' +
        (Object.keys(documentsToUpload).length === props.names.length ? 'selected ' : ' ') +
        (!props.hidden ? 'in' : 'out')
      }
    >
      <div className="forthChoiceList firstChoiceList control-group">
        <div className="row row-extend">
          <div className="col-6">
            <h2>{props.title}</h2>
            {props.descriptions?.map((desc, i) => (
              <span key={i}>
                {i + 1}. {desc} {!!documentsToUpload[props.names[i]] && <i className="icon-check"></i>} <br></br>
              </span>
            ))}
          </div>
          <div className="col-6">
            <div className="box">
              {!!(props.names.length - Object.keys(documentsToUpload).length) && (
                <div className="dropArea">
                  <input type="file" onChange={(e) => handleDrop(e)} />
                  <div className="drop">
                    <i className="icon-upload"></i>
                    <p className="textBig">Drag and drop your file</p>
                  </div>
                </div>
              )}
              {Object.keys(documentsToUpload).length > 0 && (
                <ul className="fileList">
                  {Object.keys(documentsToUpload).map((key, i) => (
                    <li key={i}>
                      ({props.names.indexOf(key) + 1}) {documentsToUpload[key].split('|')[0]}{' '}
                      <i className="icon-clear" onClick={() => removeFile(key)} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
